import React, { useCallback, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import GlobalWrapper from '../../components/Wrapper/GlobalWrapper';
import GlobalTableNoPagination from '../../components/CustomTableWithPagination/GlobalTableNoPagination';
import GlobalSelectFieldWithoutFormikNew from '../../components/mui/GlobalSelectFieldWithoutFormikNew';
import DatePickerCustom from '../../components/DatePicker/DatePickerCustom';
import GlobalDialog from '../../components/mui/Dialogue/GlobalDialog';
import { CancelButton, DeleteButton } from '../../components/mui/Buttons/Buttons';
import ChallengeFormDialog from './components/ChallengeFormDialog';
import ParticipantsDialog from './components/ParticipantsDialog';
import BandBoardDialog from './components/BandBoardDialog';
import { challengeTableHeader } from './utils/challengeTableHeader';
import {
  CHALLENGE_SCOPES,
  CHALLENGE_STATUSES,
  CHALLENGE_TYPES,
} from '../../utils/incentiveChallengeApi';
import { getChallenges, removeChallenge } from '../../redux/features/challengeSlice';

const ALL = { id: -1, value: 'All' };

/**
 * Shrinks the shared select, which has no `size` prop of its own — it only
 * forwards `sx` to its FormControl. Six filters across one row needs the height
 * back, and the label has to shrink with the box or it sits proud of it.
 */
const COMPACT_FIELD = {
  '& .MuiInputBase-root': { height: 38 },
  '& .MuiOutlinedInput-input': { fontSize: 13, paddingTop: 0, paddingBottom: 0 },
  '& .MuiInputLabel-root': { fontSize: 13, transform: 'translate(14px, 11px) scale(1)' },
  '& .MuiInputLabel-shrink': { transform: 'translate(14px, -7px) scale(0.78)' },
};

/**
 * One colour per status, used by both the count tiles and the row chips — a
 * green tile above a grey "Completed" chip would read as two different things.
 * Upcoming is amber rather than grey: it is scheduled, not inert, and grey left
 * the tile row with a dead square in it.
 */
const STATUS_COLOUR = {
  active: 'primary',
  upcoming: 'warning',
  completed: 'success',
};

/**
 * Incentive Challenges — Finance's CRUD.
 *
 * A challenge is read-only once it has ended: edit and delete are disabled with
 * a tooltip saying why, and the API refuses them too, so this is a courtesy
 * rather than the guard.
 */
export default function IncentiveChallengeList() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { list, loading, saving } = useSelector((state) => state.challenges);

  const [type, setType] = useState(ALL);
  const [scope, setScope] = useState(ALL);
  const [status, setStatus] = useState(ALL);
  // The date filter opens on the month we are in. Challenges are set and judged
  // by the month — revenue targets are issued on the 1st — so that is the window
  // Finance arrives wanting, and the ⨯ button widens it to everything.
  const [from, setFrom] = useState(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [to, setTo] = useState(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [editing, setEditing] = useState(null);
  const [cloning, setCloning] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [boardOpen, setBoardOpen] = useState(false);

  const [tableState, setTableState] = useState({
    page: 1,
    pageSize: 25,
    search: '',
    sort: { field: '', direction: '' },
    frozenColumns: ['name'],
    hiddenColumns: [],
  });

  /**
   * Filtered on the server, not here.
   *
   * Every row carries its progress, and the API works that out per challenge it
   * returns. Fetching everything and filtering in the browser meant computing
   * every challenge ever set on each visit, however narrow the date window —
   * the page would only have grown slower month by month. The date range is
   * what does the narrowing; the overlap rule ("what was running in
   * September" finds one that started in August) lives in the API.
   *
   * The table's own search box still filters in the browser: it only narrows
   * rows already here, and a request per keystroke would buy nothing.
   */
  useEffect(() => {
    dispatch(
      getChallenges({
        type: type.id !== -1 ? type.id : null,
        scope: scope.id !== -1 ? scope.id : null,
        status: status.id !== -1 ? status.id : null,
        from,
        to,
      }),
    );
  }, [dispatch, type, scope, status, from, to]);

  const rows = list || [];

  const openCreate = () => {
    setEditing(null);
    setCloning(false);
    setFormOpen(true);
  };

  const openEdit = useCallback((challenge) => {
    setEditing(challenge);
    setCloning(false);
    setFormOpen(true);
  }, []);

  // Most new challenges are last month's with new dates, so starting from an
  // existing one beats retyping seven fields. Allowed on completed challenges
  // too — re-running one that finished is the common case.
  const openClone = useCallback((challenge) => {
    setEditing(challenge);
    setCloning(true);
    setFormOpen(true);
  }, []);

  // The achievers column says how many; this says who, and how much each.
  //
  // Everyone scored separately climbs a ladder of their own, so the question is
  // always "who won which prize" — that is the board. A pooled target has one
  // number for the whole team, so its view is the contribution list instead.
  const openParticipants = useCallback((challenge) => {
    setViewing(challenge);
    if (challenge.assignmentType === 'role') setBoardOpen(true);
    else setParticipantsOpen(true);
  }, []);

  const confirmDelete = useCallback(() => {
    if (!deleting) return;
    dispatch(removeChallenge(deleting.id)).then((res) => {
      if (res.payload?.status !== 'success') {
        enqueueSnackbar(res.payload?.message || 'Could not delete the challenge', {
          variant: 'error',
        });
        return;
      }
      setDeleting(null);
      enqueueSnackbar('Challenge deleted', { variant: 'success' });
      dispatch(getChallenges());
    });
  }, [deleting, dispatch, enqueueSnackbar]);

  const renderStatus = useCallback(
    ({ row }) => (
      <Chip
        size="small"
        label={row.status.charAt(0).toUpperCase() + row.status.slice(1)}
        color={STATUS_COLOUR[row.status]}
      />
    ),
    [],
  );

  const renderAchievers = useCallback(({ row }) => {
    // Round by round: wins are counted per round, so the question becomes how
    // many rounds have been won so far, not whether one total was reached.
    if (row.partition) {
      const { rounds_won: wins, elapsed_rounds: played, total_rounds: total } = row.partition;
      if (row.isGroup) {
        return (
          <Typography variant="body2">
            {wins} <Box component="span" sx={{ color: 'text.secondary' }}>of {played} rounds won</Box>
          </Typography>
        );
      }
      return (
        <Tooltip title={`${row.achievers ?? 0} people have won at least one of ${total} rounds`} arrow>
          <Typography variant="body2">
            {row.achievers ?? 0}{' '}
            <Box component="span" sx={{ color: 'text.secondary' }}>
              / {row.participants ?? 0} · {wins} wins
            </Box>
          </Typography>
        </Tooltip>
      );
    }

    // Pooled target: the group either reached it or it did not.
    if (row.isGroup) {
      return row.teamAchieved ? (
        <Chip size="small" color="success" label="Achieved" />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {row.percent ?? 0}% of target
        </Typography>
      );
    }
    return (
      <Typography variant="body2">
        {row.achievers ?? 0} <Box component="span" sx={{ color: 'text.secondary' }}>/ {row.participants ?? 0}</Box>
      </Typography>
    );
  }, []);

  const renderActions = useCallback(
    ({ row }) => {
      // Wrapped in a span: a disabled IconButton emits no events, so a Tooltip
      // on it alone would never show the reason it is disabled.
      const lockedNote = 'Completed challenges cannot be changed';
      return (
        <Stack direction="row" spacing={0.5}>
          <Tooltip title={row.locked ? lockedNote : 'Edit'} arrow>
            <Box component="span">
              <IconButton size="small" disabled={row.locked} onClick={() => openEdit(row)}>
                <Icon icon="mdi:pencil-outline" width={18} />
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip title={row.locked ? lockedNote : 'Delete'} arrow>
            <Box component="span">
              <IconButton
                size="small"
                color="error"
                disabled={row.locked || saving}
                onClick={() => setDeleting(row)}
              >
                <Icon icon="mdi:trash-can-outline" width={18} />
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip title="Clone into a new challenge" arrow>
            <Box component="span">
              <IconButton size="small" onClick={() => openClone(row)}>
                <Icon icon="mdi:content-copy" width={17} />
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip title="See who achieved it, and by how much" arrow>
            <Box component="span">
              <IconButton size="small" onClick={() => openParticipants(row)}>
                <Icon icon="mdi:eye-outline" width={18} />
              </IconButton>
            </Box>
          </Tooltip>
        </Stack>
      );
    },
    [openEdit, openClone, openParticipants, saving],
  );

  const headers = useMemo(
    () => challengeTableHeader(renderStatus, renderAchievers, renderActions),
    [renderStatus, renderAchievers, renderActions],
  );

  const counts = useMemo(() => {
    const all = list || [];
    return {
      total: all.length,
      active: all.filter((c) => c.status === 'active').length,
      upcoming: all.filter((c) => c.status === 'upcoming').length,
      completed: all.filter((c) => c.status === 'completed').length,
    };
  }, [list]);

  return (
    <GlobalWrapper title="Incentive Challenges">
      <Box mb={1} sx={{ backgroundColor: 'white', borderRadius: '10px', p: 1 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={6} md={2}>
            <GlobalSelectFieldWithoutFormikNew
              keys={{ itemId: 'id', itemValue: 'value' }}
              data={[ALL, ...CHALLENGE_TYPES]}
              label="Type"
              fullWidth
              sx={COMPACT_FIELD}
              selectedDetails={type}
              setChange={setType}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <GlobalSelectFieldWithoutFormikNew
              keys={{ itemId: 'id', itemValue: 'value' }}
              data={[ALL, ...CHALLENGE_SCOPES]}
              label="Scope"
              fullWidth
              sx={COMPACT_FIELD}
              selectedDetails={scope}
              setChange={setScope}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <GlobalSelectFieldWithoutFormikNew
              keys={{ itemId: 'id', itemValue: 'value' }}
              data={[ALL, ...CHALLENGE_STATUSES]}
              label="Status"
              fullWidth
              sx={COMPACT_FIELD}
              selectedDetails={status}
              setChange={setStatus}
            />
          </Grid>

          {/* Any challenge running at some point between these two dates. */}
          <Grid item xs={6} md={2}>
            <DatePickerCustom
              label="From"
              fullWidth
              height="38px"
              fontSize="13px"
              value={from ? dayjs(from) : null}
              setValue={(date) => setFrom(date ? date.format('YYYY-MM-DD') : null)}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <DatePickerCustom
              label="To"
              fullWidth
              height="38px"
              fontSize="13px"
              value={to ? dayjs(to) : null}
              setValue={(date) => setTo(date ? date.format('YYYY-MM-DD') : null)}
            />
          </Grid>

          <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 1, justifyContent: { md: 'flex-end' } }}>
            {(from || to) && (
              <Tooltip title="Clear the date range" arrow>
                <IconButton size="small" onClick={() => { setFrom(null); setTo(null); }}>
                  <Icon icon="mdi:calendar-remove-outline" width={20} />
                </IconButton>
              </Tooltip>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<Icon icon="mdi:plus" width={16} />}
              onClick={openCreate}
              sx={{ whiteSpace: 'nowrap' }}
            >
              New challenge
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Counts, not headlines — they orient rather than carry the page, so
          they sit on one line each rather than as full-height cards. */}
      <Grid container spacing={1.25} mb={1.25}>
        {[
          // "All challenges" is a total rather than a status, so it stays slate
          // — colouring it would imply it meant something the others do not.
          { label: 'All challenges', value: counts.total, tone: theme.palette.grey[700] },
          { label: 'Active', value: counts.active, tone: theme.palette.primary.main },
          { label: 'Upcoming', value: counts.upcoming, tone: theme.palette.warning.main },
          { label: 'Completed', value: counts.completed, tone: theme.palette.success.main },
        ].map((tile) => (
          <Grid item xs={6} sm={3} key={tile.label}>
            <Box
              sx={{
                px: 1.75,
                py: 1.25,
                borderRadius: 1.5,
                // A tint and a left rule rather than a solid fill: four saturated
                // blocks would shout louder than the table they sit above.
                border: `1px solid ${alpha(tile.tone, 0.3)}`,
                borderLeft: `4px solid ${tile.tone}`,
                backgroundColor: alpha(tile.tone, 0.08),
              }}
            >
              <Typography variant="caption" noWrap sx={{ fontSize: 12, color: 'text.secondary' }}>
                {tile.label}
              </Typography>
              <Typography fontWeight={700} sx={{ fontSize: 24, lineHeight: 1.15, color: tile.tone }}>
                {tile.value}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>

      <GlobalTableNoPagination
        headers={headers}
        rows={rows}
        loading={loading}
        tableState={tableState}
        setTableState={setTableState}
        rowUniqueId="id"
        addSerialNo={false}
      />

      <ChallengeFormDialog
        challenge={editing}
        isClone={cloning}
        open={formOpen}
        setOpen={setFormOpen}
      />

      <ParticipantsDialog
        challenge={viewing}
        open={participantsOpen}
        setOpen={setParticipantsOpen}
      />

      <BandBoardDialog challenge={viewing} open={boardOpen} setOpen={setBoardOpen} />

      {/* A soft delete: the row stays in the table for the record, but the
          challenge is gone from every list, the widget and analytics. */}
      <GlobalDialog
        title="Delete challenge"
        open={Boolean(deleting)}
        setOpen={() => setDeleting(null)}
        fullWidth
        maxWidth="xs"
        content={
          <Typography>
            Delete &quot;{deleting?.name}&quot;? It will disappear from every list and analytics
            view.
          </Typography>
        }
        actionButton={
          <>
            <CancelButton action={() => setDeleting(null)}>Cancel</CancelButton>
            <DeleteButton action={confirmDelete}>
              {saving ? 'Deleting...' : 'Delete'}
            </DeleteButton>
          </>
        }
      />
    </GlobalWrapper>
  );
}
